import { forwardRef, type InputHTMLAttributes } from 'react';
import { cn } from '@lean-agility/design-system/react';
import { Label } from '@lean-agility/design-system/react';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /**
   * Label text displayed next to the checkbox.
   */
  label?: string;
  /**
   * Error message to display below the checkbox.
   */
  error?: string;
  /**
   * Hint text to display below the checkbox (only shown when there's no error).
   */
  hint?: string;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ className, label, error, hint, id, ...props }, ref) => {
    const checkboxId = id || (label ? label.toLowerCase().replace(/\s+/g, '-') : undefined);

    return (
      <div className="space-y-1.5">
        <div className="flex items-center gap-2">
          <input
            id={checkboxId}
            type="checkbox"
            ref={ref}
            className={cn(
              'size-4 rounded-sm border-border-strong text-action-primary',
              'focus:ring-2 focus:ring-action-primary focus:ring-offset-2',
              'transition-colors duration-200',
              'disabled:cursor-not-allowed disabled:opacity-50',
              error ? 'border-feedback-error' : '',
              className,
            )}
            {...props}
          />
          {label && (
            <Label htmlFor={checkboxId} className="cursor-pointer text-sm font-normal">
              {label}
            </Label>
          )}
        </div>
        {error && <p className="text-xs text-feedback-error">{error}</p>}
        {hint && !error && <p className="text-xs text-text-muted">{hint}</p>}
      </div>
    );
  },
);

Checkbox.displayName = 'Checkbox';
